import { crossSellCardPriceText } from '~/utils/shop/crossSellCardPriceText';
import {
  buildCandidatesForRecommendedSku,
  indexProductsBySku,
  type ProductsWithParentVariantsItem
} from '~/utils/shop/buildActionBarCrossSellCandidates';
import {
  selectActionBarCrossSellProduct,
  type ActionBarCrossSellSelection,
  type ActionBarCrossSellVariant
} from '~/utils/shop/selectActionBarCrossSellProduct';
import {
  getGroupId,
  type RecommendedProductGroup
} from '~/stores/productDetailPageStore/crossSellingProductsStore';
import { isDefined } from '~/utils/guards/isDefined';
import type { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import { resolveProductImageUrlFromJson } from '~/utils/shop/resolveProductImageUrlFromJson';

export type ActionBarCrossSellCardModel = {
  groupId: string;
  groupLabel: string;
  /** Recommended-product SKU that produced this card (PDP dropdown value). */
  productSku: string;
  displayName: string;
  selectedVariantSku: string;
  variants: ActionBarCrossSellVariant[];
  priceDeltaText: string;
  imageJson: string | null;
  imageUrl: string | null;
  /** Product the BVS simulation renders with — shown first and framed. */
  highlighted?: boolean;
};

export type ActionBarCrossSellBuildResult = {
  cards: ActionBarCrossSellCardModel[];
};

function resolveDisplayName(
  selection: ActionBarCrossSellSelection,
  selectedSku: string
): string {
  const selected =
    selection.variants.find((variant) => variant.sku === selectedSku) ??
    selection.variants[0];
  const name = selected?.name ?? selectedSku;
  // Strip trailing length for card title when multiple lengths exist
  if (selection.variants.length > 1 && selected?.lengthLabel) {
    return (
      name
        .replace(
          new RegExp(`,?\\s*${escapeRegExp(selected.lengthLabel)}$`, 'i'),
          ''
        )
        .trim() || name
    );
  }
  return name;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function priceDeltaForVariant(
  variant: ActionBarCrossSellVariant | undefined,
  locale: string
): string {
  return crossSellCardPriceText({
    priceValue: variant?.priceValue,
    currency: variant?.currency,
    locale,
    fallbackText: ''
  });
}

export function sortCrossSellCards(
  cards: ActionBarCrossSellCardModel[],
  browsedGroupIds: Readonly<Record<string, true>>
): ActionBarCrossSellCardModel[] {
  const browsedKeys = Object.keys(browsedGroupIds);
  if (browsedKeys.length === 0) {
    return cards;
  }

  const allBrowsed = cards.every((card) => browsedGroupIds[card.groupId]);
  if (allBrowsed) {
    return cards;
  }

  return [...cards].sort((a, b) => {
    const aBrowsed = browsedGroupIds[a.groupId] ? 1 : 0;
    const bBrowsed = browsedGroupIds[b.groupId] ? 1 : 0;
    return aBrowsed - bBrowsed;
  });
}

export function buildActionBarCrossSellCards(params: {
  groups: ReadonlyArray<RecommendedProductGroup>;
  stackSkus: ReadonlyArray<string>;
  skuToGroupIdMap: Readonly<Record<string, string>>;
  productsBySku: ReadonlyMap<string, ProductsWithParentVariantsItem>;
  mainProductShippingDays: number | null;
  browsedGroupIds: Readonly<Record<string, true>>;
  locale: string;
}): ActionBarCrossSellCardModel[] {
  const occupiedGroupIds = new Set(
    params.stackSkus.map((sku) => params.skuToGroupIdMap[sku]).filter(isDefined)
  );

  const cards: ActionBarCrossSellCardModel[] = [];

  for (const group of params.groups) {
    if (!group) {
      continue;
    }
    const groupId = getGroupId(group);
    if (occupiedGroupIds.has(groupId)) {
      continue;
    }

    const products = group.products?.filter(isDefined) ?? [];
    const candidates = products
      .map((product) => {
        const sku = product.sku;
        if (!sku) {
          return null;
        }
        return buildCandidatesForRecommendedSku(sku, params.productsBySku);
      })
      .filter(isDefined);

    const selection = selectActionBarCrossSellProduct(
      candidates,
      params.mainProductShippingDays
    );
    if (!selection) {
      continue;
    }

    const selectedVariant =
      selection.variants.find(
        (variant) => variant.sku === selection.selectedVariantSku
      ) ?? selection.variants[0];

    const imageJson = selectedVariant?.imageJson ?? null;

    cards.push({
      groupId,
      groupLabel: group.label ?? group.name ?? '',
      productSku: selection.productSku,
      displayName: resolveDisplayName(selection, selection.selectedVariantSku),
      selectedVariantSku: selection.selectedVariantSku,
      variants: selection.variants,
      priceDeltaText: priceDeltaForVariant(selectedVariant, params.locale),
      imageJson,
      imageUrl: resolveProductImageUrlFromJson(imageJson)
    });
  }

  return sortCrossSellCards(cards, params.browsedGroupIds);
}

export function mainProductShippingDaysFromDecorated(
  product: DecoratedProduct | null | undefined
): number | null {
  if (!product) {
    return null;
  }
  if (!product.showShippingTime || product.shippingTime <= 0) {
    return null;
  }
  return product.shippingTime;
}

export async function fetchProductsWithParentVariantsBySkus(
  skus: string[],
  store: string,
  locale: string
): Promise<ProductsWithParentVariantsItem[]> {
  if (skus.length === 0) {
    return [];
  }

  const response = await $fetch<{
    items?: Array<ProductsWithParentVariantsItem | null>;
  }>('/api/magento/productsWithParentVariantsBySkus', {
    query: {
      store,
      locale,
      skus,
      pageSize: String(skus.length + 1)
    }
  });

  return (response?.items ?? []).filter(isDefined);
}

export { indexProductsBySku };
